/**
 * Phase 19 F12 (P1-5): one goal structure for every discipline's care plan, with status and
 * progress (F00120 §1 "progress toward goals", payer plan-of-care reviews).
 *
 * Stored at carePlan.data.goals. Plans saved before Phase 19 keep goals in discipline-specific
 * fields; `legacyGoalsToArray` converts them once when the plan opens, and `clearedLegacyGoalFields`
 * blanks those fields on save so a goal never lives in two places. The backend reads data.goals
 * when present (patientmr/pocModel.js goalsFromCarePlan) and the same legacy fields otherwise.
 */

export type GoalTerm = 'SHORT' | 'LONG';
export type GoalStatus = 'ACTIVE' | 'MET' | 'PARTIALLY_MET' | 'NOT_MET' | 'DISCONTINUED';
export type CarePlanGoalDiscipline = 'RN' | 'PT' | 'OT' | 'ST';

export interface CarePlanGoal {
  goalId: string;
  domain: string;
  text: string;
  term: GoalTerm | null;
  baseline: string;
  measure: string;
  targetDate: string;
  status: GoalStatus;
  statusDate: string;
  progressNote: string;
}

export const GOAL_STATUS_LABELS: Record<GoalStatus, string> = {
  ACTIVE: 'Active',
  MET: 'Met',
  PARTIALLY_MET: 'Partially met',
  NOT_MET: 'Not met',
  DISCONTINUED: 'Discontinued',
};

export const GOAL_DOMAINS: Record<CarePlanGoalDiscipline, string[]> = {
  RN: ['Respiratory', 'Tracheostomy', 'Ventilator', 'Seizure', 'Feeding / nutrition', 'Medication', 'Safety', 'Skin / wound care',
    'Pain management', 'Self-care', 'Caregiver education', 'Other'],
  PT: ['Mobility', 'Strength', 'Balance', 'Functional mobility', 'Gross motor', 'Positioning', 'Caregiver education', 'Other'],
  OT: ['Self-care', 'Fine motor', 'Sensory processing', 'Feeding', 'Visual-motor', 'Play skills', 'Upper extremity function',
    'Caregiver education', 'Other'],
  ST: ['Receptive language', 'Expressive language', 'Speech production', 'Swallowing', 'AAC', 'Pragmatics', 'Voice', 'Fluency',
    'Cognitive-communication', 'Caregiver education', 'Other'],
};

let counter = 0;
export function newGoalId(): string {
  counter += 1;
  const rand = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${counter}`;
  return `goal-${rand}`;
}

export function emptyGoal(domain = ''): CarePlanGoal {
  return { goalId: newGoalId(), domain, text: '', term: null, baseline: '', measure: '', targetDate: '', status: 'ACTIVE', statusDate: '', progressNote: '' };
}

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Legacy fields that held goals, per discipline: [key, domain, term, targetDateKey?, measureKey?, typeKey?] */
type LegacyGoalField = [string, string, GoalTerm | null, string?, string?, string?];

const RN_DOMAIN_FIELDS: LegacyGoalField[] = [
  ['goalSelfCare', 'Self-care', null, 'goalSelfCareTargetDate', 'goalSelfCareMeasurable', 'goalSelfCareType'],
  ['goalMedication', 'Medication', null, 'goalMedicationTargetDate', 'goalMedicationMeasurable', 'goalMedicationType'],
  ['goalSafety', 'Safety', null, 'goalSafetyTargetDate', 'goalSafetyMeasurable', 'goalSafetyType'],
  ['goalEducation', 'Caregiver education', null, 'goalEducationTargetDate', 'goalEducationMeasurable', 'goalEducationType'],
  ['goalWoundCare', 'Skin / wound care', null, 'goalWoundCareTargetDate', 'goalWoundCareMeasurable', 'goalWoundCareType'],
  ['goalPainManagement', 'Pain management', null, 'goalPainManagementTargetDate', 'goalPainManagementMeasurable', 'goalPainManagementType'],
  ['goalNutrition', 'Feeding / nutrition', null, 'goalNutritionTargetDate', 'goalNutritionMeasurable', 'goalNutritionType'],
  ['goalOther', 'Other', null, 'goalOtherTargetDate', 'goalOtherMeasurable', 'goalOtherType'],
  ['pdnTracheostomyGoal', 'Tracheostomy', null], ['pdnVentilatorGoal', 'Ventilator', null], ['pdnRespiratoryGoal', 'Respiratory', null],
  ['pdnSeizureGoal', 'Seizure', null], ['pdnFeedingGoal', 'Feeding / nutrition', null], ['pdnMedicationGoal', 'Medication', null],
  ['pdnCaregiverEducationGoal', 'Caregiver education', null],
];

export const LEGACY_GOAL_FIELDS: Record<CarePlanGoalDiscipline, LegacyGoalField[]> = {
  RN: RN_DOMAIN_FIELDS,
  PT: [
    ['goalMobilityShortTerm', 'Mobility', 'SHORT', 'goalMobilityShortTermTargetDate', 'goalMobilityShortTermMeasurable'],
    ['goalStrengthShortTerm', 'Strength', 'SHORT', 'goalStrengthShortTermTargetDate', 'goalStrengthShortTermMeasurable'],
    ['goalBalanceShortTerm', 'Balance', 'SHORT', 'goalBalanceShortTermTargetDate', 'goalBalanceShortTermMeasurable'],
    ['goalMobilityLongTerm', 'Mobility', 'LONG', 'goalMobilityLongTermTargetDate', 'goalMobilityLongTermMeasurable'],
    ['goalStrengthLongTerm', 'Strength', 'LONG', 'goalStrengthLongTermTargetDate', 'goalStrengthLongTermMeasurable'],
    ['goalFunctionalMobilityLongTerm', 'Functional mobility', 'LONG', 'goalFunctionalMobilityLongTermTargetDate', 'goalFunctionalMobilityLongTermMeasurable'],
    ['goalCaregiverEducation', 'Caregiver education', null, 'goalCaregiverEducationTargetDate', 'goalCaregiverEducationMeasurable'],
  ],
  OT: [
    ['goalSelfCare', 'Self-care', null], ['goalFineMotor', 'Fine motor', null], ['goalSensoryProcessing', 'Sensory processing', null],
    ['goalFeeding', 'Feeding', null], ['goalVisualMotor', 'Visual-motor', null], ['goalCaregiverEducation', 'Caregiver education', null],
    ['goalCognitive', 'Other', null], ['goalPlaySkills', 'Play skills', null], ['goalWritingSkills', 'Fine motor', null],
    ['goalUpperExtremityFunction', 'Upper extremity function', null], ['goalOther', 'Other', null],
    ['pediatricFineMotorGoal1', 'Fine motor', null], ['pediatricFineMotorGoal2', 'Fine motor', null], ['pediatricFineMotorGoal3', 'Fine motor', null],
    ['pediatricSensoryProcessingGoal', 'Sensory processing', null], ['pediatricSelfCareGoal', 'Self-care', null],
    ['pediatricFeedingGoal', 'Feeding', null], ['pediatricVisualMotorGoal', 'Visual-motor', null], ['pediatricPlaySkillsGoal', 'Play skills', null],
    ['pdnSensoryGoal', 'Sensory processing', null], ['pdnFeedingOTGoal', 'Feeding', null],
  ],
  ST: [
    ['shortTermGoals', 'Other', 'SHORT'], ['longTermGoals', 'Other', 'LONG'],
    ['goalReceptiveLanguage', 'Receptive language', null], ['goalExpressiveLanguage', 'Expressive language', null],
    ['goalSpeechProduction', 'Speech production', null], ['goalSwallowing', 'Swallowing', null],
    ['goalCognitiveLanguage', 'Cognitive-communication', null], ['goalVoice', 'Voice', null], ['goalFluency', 'Fluency', null],
    ['goalPragmatics', 'Pragmatics', null], ['goalAAC', 'AAC', null], ['goalCaregiverEducation', 'Caregiver education', null],
    ['goalOther', 'Other', null],
    ['pediatricLanguageGoal1', 'Expressive language', null], ['pediatricLanguageGoal2', 'Expressive language', null],
    ['pediatricLanguageGoal3', 'Expressive language', null], ['pediatricSpeechGoal', 'Speech production', null],
    ['pediatricPragmaticGoal', 'Pragmatics', null], ['pediatricFeedingGoal', 'Swallowing', null], ['pediatricAACGoal', 'AAC', null],
    ['pdnSwallowingGoal', 'Swallowing', null], ['pdnAACGoal', 'AAC', null],
  ],
};

/** Goals held in the legacy fields of a plan saved before Phase 19. */
export function legacyGoalsToArray(discipline: CarePlanGoalDiscipline, data: Record<string, unknown>): CarePlanGoal[] {
  const goals: CarePlanGoal[] = [];
  for (const [key, domain, term, dateKey, measureKey, typeKey] of LEGACY_GOAL_FIELDS[discipline]) {
    const text = str(data[key]);
    if (!text) continue;
    const type = typeKey ? str(data[typeKey]) : '';
    goals.push({
      ...emptyGoal(domain),
      text,
      term: type === 'long_term' ? 'LONG' : type === 'short_term' ? 'SHORT' : term,
      targetDate: dateKey ? str(data[dateKey]) : '',
      measure: measureKey ? str(data[measureKey]) : '',
    });
  }
  return goals;
}

/** The goals a plan opens with: data.goals when saved, otherwise converted legacy fields. */
export function initialGoals(discipline: CarePlanGoalDiscipline, data: Record<string, unknown> | null | undefined): CarePlanGoal[] {
  if (!data) return [];
  if (Array.isArray(data.goals)) {
    return (data.goals as Partial<CarePlanGoal>[]).map(g => ({ ...emptyGoal(), ...g, goalId: g.goalId || newGoalId() }));
  }
  return legacyGoalsToArray(discipline, data);
}

/** Blank values for every legacy goal field (spread into the saved data). */
export function clearedLegacyGoalFields(discipline: CarePlanGoalDiscipline): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, , , dateKey, measureKey] of LEGACY_GOAL_FIELDS[discipline]) {
    out[key] = '';
    if (dateKey) out[dateKey] = '';
    if (measureKey) out[measureKey] = '';
  }
  return out;
}

/** Validation for submit: at least one complete goal; a goal that is no longer active needs a note. */
export function goalErrors(goals: CarePlanGoal[]): Record<string, string> {
  const errors: Record<string, string> = {};
  const complete = goals.filter(g => g.text.trim() && g.measure.trim() && g.targetDate);
  if (!complete.length) errors.goals = 'Add at least one goal with a measurable outcome and a target date';
  for (const g of goals) {
    if (g.text.trim() && g.status !== 'ACTIVE' && !g.progressNote.trim()) {
      errors[`goal-${g.goalId}`] = 'Add a progress note when a goal is met, not met or discontinued';
    }
  }
  return errors;
}
